import React, { ReactNode } from 'react'

import { faWifiExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import Cookies from 'js-cookie'
import { get, inRange, isEmpty } from 'lodash'
import queryString from 'query-string'

import { Flex, Typography } from 'antd'

import { showErrorModal } from '../components/modal/methods'
import safeJSONParse from '../utils/safeJSONParse'

export const CONTACT_ADMINISTRATOR_MESSAGE =
  'Please contact your system administrator.'
export const UNKNOWN_SERVER_ERROR_MESSAGE = `Server Error! ${CONTACT_ADMINISTRATOR_MESSAGE}`
export const UNKNOWN_ERROR_MESSAGE =
  'Something went wrong. Please try to refresh the page.'
export const NO_INTERNET_CONNECTION_ERROR_MESSAGE =
  'No Internet Connection!\nCheck your network settings and try again.'

export const handleServerError = (
  err: any,
  ignoredValuesList: any,
  errorFieldsMap: any = {}
) => {
  if (!isEmpty(get(err, 'errors', {}))) {
    return Object.keys(err.errors)
      .map((key) => {
        const fieldName = !ignoredValuesList.includes(key)
          ? `${get(errorFieldsMap, [key], key)}: `
          : ''

        return `${fieldName}${get(err, ['errors', key, '0'], '')}`
      })
      .join(' ')
  } else {
    return get(
      err,
      ['message'],
      get(
        err,
        ['error_description'],
        get(err, ['error'], UNKNOWN_SERVER_ERROR_MESSAGE)
      )
    )
  }
}

export const catchError = (err = {}, props = {}) => {
  const { status, headers, noInternetConnection } = err
  const { ignoredValuesList = [], errorFieldsMap = {} } = props
  let title = 'Error'

  if (noInternetConnection) return

  if (inRange(status, 400, 499)) {
    title = 'Validation Error'
  }

  let message =
    handleServerError(err, ignoredValuesList, errorFieldsMap) ||
    UNKNOWN_ERROR_MESSAGE
  let icon: ReactNode

  if (
    [400, 401].includes(status) &&
    [
      'Your session expired.',
      'Full authentication is required to access this resource.'
    ].includes(message)
  ) {
    return
  }

  if (!status) {
    message = UNKNOWN_ERROR_MESSAGE
  }

  if (status && status >= 500) {
    message = UNKNOWN_SERVER_ERROR_MESSAGE
  }

  if ([408, 504].includes(status)) {
    message =
      "We couldn't complete your request due to a slow or unstable internet connection. Please check your connection."
    icon = (
      <FontAwesomeIcon icon={faWifiExclamation} fontSize={52} color="#d95f5f" />
    )
  }

  const activeModals = Array.from(
    document.querySelectorAll('div.ant-modal-root .ant-modal-confirm-content')
  ).map((node) => node?.textContent)

  const content =
    inRange(status, 499, 600) && headers?.['apigw-requestid'] ? (
      <Flex vertical gap="small">
        <Typography.Text strong>
          Error Id: {headers['apigw-requestid']}
        </Typography.Text>
        {message}
      </Flex>
    ) : (
      message
    )

  if (!activeModals.includes(message)) {
    showErrorModal({
      title,
      content,
      centered: true,
      icon,
      ...props
    })
  }
}

class APIError extends Error {
  constructor({ message, error, ...restProps }) {
    super(message || error)

    for (const prop in restProps) {
      this[prop] = restProps[prop]
    }
  }
}

class HttpClient {
  axios: any
  refreshRequest: null
  store: { dispatch?: any }
  onLogout: () => void
  onNoInternetConnection: (status: boolean) => void
  onRefreshToken: (token: string) => void

  // constructor() {}

  setupAxios({ API_URL, API_HEADERS }) {
    this.axios = axios.create({
      headers: API_HEADERS,
      baseURL: API_URL
    })
    this.refreshRequest = null

    this.axios.interceptors.request.use(
      (config) => {
        const accessToken = Cookies.get('access_token')
        if (!accessToken || config.skipAuth) {
          return config
        }

        const { headers = {} } = config
        const newConfig = {
          headers,
          ...config
        }

        newConfig.headers.Authorization = `Bearer ${accessToken}`
        return newConfig
      },
      (error: any) => Promise.reject(error)
    )

    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { dispatch } = this.store
        const config = get(error, ['config'], null)
        const response = get(error, ['response'], null)
        const status = get(response, ['status'], null)
        const data = get(response, ['data'], null)
        const headers = get(response, ['headers'], {})
        const refreshToken = Cookies.get('refresh_token')
        let originalError = { status, ...safeJSONParse(data) }

        if (!response) {
          if (!config?.skipOnNoInternetConnection) {
            this.onNoInternetConnection &&
              dispatch(this.onNoInternetConnection(true))
          }
          // eslint-disable-next-line prefer-promise-reject-errors
          throw new APIError({
            ...originalError,
            noInternetConnection: true,
            headers,
            message: NO_INTERNET_CONNECTION_ERROR_MESSAGE
          })
        }

        if (data instanceof Blob) {
          originalError = { status, ...safeJSONParse(await data.text()) }
        }

        if (status === 401 && !config?.skipAuth) {
          if (!refreshToken) {
            await dispatch(this.onLogout())
            throw new APIError({ ...originalError, headers })
          }

          try {
            if (!this.refreshRequest) {
              this.refreshRequest = dispatch(this.onRefreshToken(refreshToken))
            }

            await this.refreshRequest
            const newRequest = {
              ...config,
              retry: true
            }

            return this.axios(newRequest)
          } catch (_) {
            await dispatch(this.onLogout())
            throw new APIError({ ...originalError, headers })
          } finally {
            this.refreshRequest = null
          }
        }

        throw new APIError({ ...originalError, headers })
      }
    )
  }

  initialize({
    store,
    onLogout,
    onNoInternetConnection,
    onRefreshToken,
    API_URL,
    API_HEADERS
  }) {
    this.store = store
    this.onLogout = onLogout
    this.onNoInternetConnection = onNoInternetConnection
    this.onRefreshToken = onRefreshToken

    this.setupAxios({
      API_URL,
      API_HEADERS
    })
  }

  static normalizeRoute(route = {}) {
    const normalizedRoute = {}

    if (typeof route === 'string') {
      normalizedRoute.pathname = route
    } else if (typeof route === 'object' && route) {
      normalizedRoute.pathname = route.pathname
      normalizedRoute.params = route.params
      normalizedRoute.query = route.query
    }

    return normalizedRoute
  }

  static injectStringParams = (str, params = {}) =>
    str.replace(/:(\w+)/g, (_, param) => params[param])

  static composeRequestURL({ pathname, params, query }) {
    const url = this.injectStringParams(pathname, params)
    const processedQuery = queryString.stringify(query)

    return processedQuery ? `${url}?${processedQuery}` : url
  }

  async request({
    method = 'get',
    url = '',
    payload = {},
    customHeaders = {},
    isDownload = false,
    skipOnNoInternetConnection = false,
    skipAuth = false
  }: {
    method?: string
    url?: any
    payload?: any
    customHeaders?: any
    isDownload?: boolean
    skipOnNoInternetConnection?: boolean
    skipAuth?: boolean
  }) {
    const route = HttpClient.composeRequestURL(HttpClient.normalizeRoute(url))

    if (!isEmpty(customHeaders)) {
      Object.keys(customHeaders).forEach((header: string) => {
        this.axios.defaults.headers[header] = customHeaders[header]
      })
    }

    this.axios.defaults.headers['Content-Type'] = 'application/json'

    const config = { skipOnNoInternetConnection, skipAuth }

    const response = isDownload
      ? await this.axios.get(route, { responseType: 'blob', ...config })
      : method === 'delete'
      ? await this.axios.delete(route, { data: payload, ...config })
      : method === 'get'
      ? await this.axios.get(route, config)
      : await this.axios[method](route, payload, config)

    return isDownload ? response : response?.data
  }

  async upload({ url, data }) {
    const route = HttpClient.composeRequestURL(HttpClient.normalizeRoute(url))

    this.axios.defaults.headers['Content-Type'] =
      'application/x-www-form-urlencoded'

    const response = await this.axios.post(route, data)

    return response
  }

  // legacy
  get(url, customHeaders) {
    return this.request({ method: 'get', url, customHeaders })
  }

  post(url, payload, customHeaders) {
    return this.request({ method: 'post', url, payload, customHeaders })
  }

  patch(url, payload, customHeaders) {
    return this.request({ method: 'patch', url, payload, customHeaders })
  }

  delete(url, payload, customHeaders) {
    return this.request({ method: 'delete', url, payload, customHeaders })
  }
}

export default new HttpClient()
